/**
 * 微信授权工具类
 *
 * @see https://developers.weixin.qq.com/doc/service/guide/h5/auth.html
 */

/**
 * 授权范围
 * - snsapi_base: 静默授权，只能获取 openid
 * - snsapi_userinfo: 需要用户确认，可获取用户基本信息
 */
export type WeixinAuthScope = 'snsapi_base' | 'snsapi_userinfo';

/**
 * 微信网页授权工具类
 */
class WeixinAuth {
  /** 微信服务号号 AppID */
  private wechatMpAppId: string = import.meta.env.VITE_WECHAT_MP_APPID || '';

  /**
   * 构造微信授权URL
   * @param redirectUri 授权后重定向的回调地址，注意不要 encodeURIComponent
   * @param scope 授权范围
   * @param state 可选，重定向后会带上state参数，开发者可以填写a-zA-Z0-9的参数值，最多128字节
   * @returns 授权URL
   */
  buildAuthUrl(redirectUri: string, scope: WeixinAuthScope, state?: string): string {
    if (!this.wechatMpAppId) {
      throw new Error('微信服务号 AppID 未配置，请在环境变量中设置 VITE_WECHAT_MP_APPID');
    }

    const encodedRedirectUri = encodeURIComponent(redirectUri);
    const stateParam = state ? `&state=${encodeURIComponent(state)}` : '';

    // forcePopup=true 强制弹窗，可以模拟快照模式
    return `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${this.wechatMpAppId}&redirect_uri=${encodedRedirectUri}&response_type=code&scope=${scope}${stateParam}#wechat_redirect`;
  }

  /**
   * 发起微信授权
   * @param redirectUri 授权后重定向的回调地址
   * @param scope 授权范围
   * @param state 可选，状态参数
   */
  authorize(redirectUri: string, scope: WeixinAuthScope, state?: string): void {
    const authUrl = this.buildAuthUrl(redirectUri, scope, state);
    // 使用 replace 避免增加历史记录，方便检测用户拒绝授权
    window.location.replace(authUrl);
  }
}

export const weixinAuth = new WeixinAuth();

export default weixinAuth;
